import type { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../features/auth/AuthContext';
import { useAnnouncer } from './LiveAnnouncer';
import { api } from '../lib/api';

/** Campanella con contatore non lette (aggiornata ogni minuto). */
function NotificationBell({
  className,
}: {
  className: (props: { isActive: boolean }) => string;
}) {
  const unread = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api<{ unread: number }>('/notifications').then((r) => r.unread),
    refetchInterval: 60_000,
  });
  const count = unread.data ?? 0;

  return (
    <NavLink
      to="/notifiche"
      className={className}
      aria-label={`Notifiche${count > 0 ? `, ${count} non lette` : ''}`}
    >
      <span aria-hidden="true">🔔{count > 0 ? ` ${count}` : ''}</span>
    </NavLink>
  );
}

/**
 * Layout per le pagine autenticate: skip link, header con nav landmark,
 * main con id per il salto, footer. La voce di menu corrente è marcata
 * con aria-current="page" (gestito da NavLink).
 */
export function AppLayout({ title, children }: { title: string; children: ReactNode }) {
  const { user, logout } = useAuth();
  const { announce } = useAnnouncer();
  const navigate = useNavigate();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-2 font-medium no-underline ${
      isActive
        ? 'bg-panda-100 text-panda-900 dark:bg-panda-900 dark:text-panda-100'
        : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800'
    }`;

  return (
    <>
      <a href="#contenuto" className="skip-link">
        Salta al contenuto principale
      </a>
      <div className="flex min-h-screen flex-col">
        <header className="border-b border-ink-200 dark:border-ink-800">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-3">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-xl font-bold no-underline">
              <img src="/favicon.png" alt="" width={36} height={36} className="h-9 w-9" />
              PandaCalcio
            </Link>
            {/* Una sola riga anche su mobile: scorre in orizzontale invece di
                spezzarsi su due righe con "Esci" orfano. */}
            <nav
              aria-label="Navigazione principale"
              className="flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap"
            >
              <NavLink to="/dashboard" className={navLinkClass}>
                Dashboard
              </NavLink>
              {/* Su schermi stretti si crea la lega dalla dashboard */}
              <NavLink
                to="/leghe/nuova"
                className={(p) => `hidden sm:inline-block ${navLinkClass(p)}`}
              >
                Crea lega
              </NavLink>
              <NotificationBell className={navLinkClass} />
              <NavLink to="/profilo" className={navLinkClass}>
                Profilo
              </NavLink>
              {user?.globalRole === 'SUPER_ADMIN' && (
                <NavLink to="/admin" className={navLinkClass}>
                  Amministrazione
                </NavLink>
              )}
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  announce('Sei uscito dal tuo account.');
                  navigate('/');
                }}
                className="rounded-lg px-3 py-2 font-medium text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800"
              >
                Esci
                <span className="visually-hidden"> dall’account di {user?.displayName}</span>
              </button>
            </nav>
          </div>
        </header>

        <main id="contenuto" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          {children}
        </main>

        <footer className="border-t border-ink-200 px-4 py-4 dark:border-ink-800">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 text-sm text-ink-600 dark:text-ink-300">
            <p>PandaCalcio — pronostici accessibili a tutti.</p>
            <nav aria-label="Collegamenti utili" className="flex flex-wrap gap-x-4">
              {/* min-h-11: tap target da 44px anche per i link secondari */}
              <Link to="/installa" className="inline-flex min-h-11 items-center underline">Installa l’app</Link>
              <Link to="/privacy" className="inline-flex min-h-11 items-center underline">Privacy</Link>
              <Link to="/cookie" className="inline-flex min-h-11 items-center underline">Cookie</Link>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
}
